// lineup.js - the coach-page Line-up manager. A coach records a match line-up
// (formation + starting slots + who subbed on / sat out) on a branded field,
// and sees a season participation rollup. Standalone lens - never a Dragons
// Score input (director decision 2026-06-29). Field geometry + formations come
// from lineup-data.js (ported from the khc-tactical-diagrams skill).

import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { DB } from './db.js';
import {
  FORMATION_GROUPS, formationSize, slotPositions,
  participationRollup, mainRole,
} from './lineup-data.js';
import { hockeyMarkings } from './field-svg.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const LINEUP = (() => {

  let _team = null;
  let _onBack = null;
  let _lineups = [];       // this team+season's matches
  let _roster = [];        // [{ id, name, is_goalkeeper }]
  let _participation = []; // spot rows for the rollup

  // Editor state.
  let _edit = null;        // { id, formation, match_date, opponent }
  let _assign = new Map(); // slot index -> player_id (starters)
  let _subs = new Map();   // player_id -> [tags] (the lines a substitute can play)

  // The position tags a coach can put on a substitute (director 2026-07-13).
  const SUB_TAGS = ['Defence', 'Midfield', 'Forward'];
  const SUB_ABBR = { Defence: 'Def', Midfield: 'Mid', Forward: 'Fwd' };

  async function showTeam(team, onBack) {
    _team = team; _onBack = onBack;
    UI.showScreen('screen-lineup');
    document.getElementById('lineup-title').textContent = t('lu.title');
    document.getElementById('lineup-sub').textContent = team.id;
    UI.setLoading('lineup-content', t('common.loading'));
    try {
      const season = CONFIG.season.current;
      const [lineups, roster, participation] = await Promise.all([
        DB.lineups.listForTeam(team.id, season),
        DB.players.getByTeam(team.id, season),
        DB.lineups.participationForTeam(team.id, season).catch(() => []),
      ]);
      _lineups = lineups || [];
      _roster = (roster || []).map(m => ({ id: m.players.id, name: m.players.name, is_goalkeeper: !!m.players.is_goalkeeper }))
        .sort((a, b) => a.name.localeCompare(b.name));
      _participation = participation || [];
      _renderList();
    } catch (err) {
      console.error('Line-up load failed:', err);
      UI.setEmpty('lineup-content', t('common.error'));
    }
  }

  function backFromList() { if (_onBack) _onBack(); }

  // ── List: matches + participation ───────────────────────────────────────────

  function _renderList() {
    const el = document.getElementById('lineup-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;

    const matches = _lineups.length
      ? _lineups.map(l => `
          <button class="player-eval-card" data-open="${esc(l.id)}">
            <div class="player-eval-card__main">
              <div class="player-eval-card__title">${esc(UTILS.formatDate(l.match_date, 'medium'))}${l.opponent ? ' · ' + esc(l.opponent) : ''}</div>
              <div class="player-eval-card__sub">${esc(l.formation)}</div>
            </div>
            <span class="player-eval-card__chevron">›</span>
          </button>`).join('')
      : `<div class="sel-detail-hint">${esc(t('lu.noneYet'))}</div>`;

    // Participation rollup (season).
    const roll = participationRollup(_participation);
    const nameById = Object.fromEntries(_roster.map(p => [p.id, p.name]));
    const partRows = [..._roster]
      .map(p => ({ p, r: roll.get(p.id) }))
      .filter(x => x.r)
      .sort((a, b) => b.r.played - a.r.played)
      .map(({ p, r }) => `
        <tr>
          <td class="rk-name">${esc(nameById[p.id] || p.name)}</td>
          <td class="rk-n">${r.starts}</td>
          <td class="rk-n">${r.subs}</td>
          <td class="rk-n">${r.out}</td>
          <td>${esc(mainRole(r) || '-')}</td>
        </tr>`).join('');

    el.innerHTML = `
      <div class="sel-detail-actions" style="margin-bottom:12px;">
        <button class="btn btn--primary" id="lineup-new">${esc(t('lu.new'))}</button>
      </div>
      <div class="sel-detail-section__title" style="margin-bottom:8px;">${esc(t('lu.matches'))}</div>
      ${matches}
      ${partRows ? `
        <div class="sel-detail-section__title" style="margin:18px 0 8px;">${esc(t('lu.participation'))}</div>
        <div class="rk-tablewrap">
          <table class="rk-table">
            <thead><tr>
              <th class="rk-th">${esc(t('lu.player'))}</th>
              <th class="rk-th">${esc(t('lu.started'))}</th>
              <th class="rk-th">${esc(t('lu.sub'))}</th>
              <th class="rk-th">${esc(t('lu.out'))}</th>
              <th class="rk-th">${esc(t('lu.mainRole'))}</th>
            </tr></thead>
            <tbody>${partRows}</tbody>
          </table>
        </div>` : ''}
    `;

    document.getElementById('lineup-new')?.addEventListener('click', _newLineup);
    el.querySelectorAll('[data-open]').forEach(b =>
      b.addEventListener('click', () => _openEditor(b.dataset.open)));
  }

  // ── New line-up: date + formation, then create + open the editor ────────────

  function _newLineup() {
    const esc = UTILS.escapeHtml;
    const groups = FORMATION_GROUPS.map(g => `
      <div class="gate__label" style="margin-top:10px;">${esc(g.label)}</div>
      <div class="sel-section-toggles">
        ${g.keys.map(k => `<button class="sel-section-toggle" data-formation="${esc(k)}">${esc(k)}</button>`).join('')}
      </div>`).join('');
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" style="max-width:440px;">
        <div class="modal__title">${esc(t('lu.newTitle'))}</div>
        <div class="modal__body">
          <div class="gate__label">${esc(t('lu.date'))}</div>
          <input class="field" id="lu-date" type="date" value="${esc(UTILS.today())}">
          <div class="gate__label" style="margin-top:10px;">${esc(t('lu.opponent'))}</div>
          <input class="field" id="lu-opponent" placeholder="${esc(t('lu.opponentPh'))}">
          <div class="gate__label" style="margin-top:10px;">${esc(t('lu.formation'))}</div>
          ${groups}
        </div>
        <div class="modal__buttons"><button class="modal-btn" data-action="cancel">${esc(t('common.cancel'))}</button></div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('[data-action="cancel"]').addEventListener('click', close);
    overlay.querySelectorAll('[data-formation]').forEach(b =>
      b.addEventListener('click', async () => {
        const formation = b.dataset.formation;
        const match_date = document.getElementById('lu-date').value || UTILS.today();
        const opponent = (document.getElementById('lu-opponent').value || '').trim() || null;
        close();
        try {
          const row = await DB.lineups.create({
            team_id: _team.id, season_id: CONFIG.season.current,
            match_date, opponent, formation,
            author_staff_id: AUTH.isStaff() ? AUTH.getStaffId() : null,
          });
          if (!row?.id) throw new Error('no id');
          _lineups.unshift(row);
          _openEditor(row.id);
        } catch (err) {
          console.error('Create line-up failed:', err);
          UI.toast(t('common.error'), 'error');
        }
      }));
  }

  // ── Editor: the field + bench ───────────────────────────────────────────────

  async function _openEditor(lineupId) {
    const l = _lineups.find(x => x.id === lineupId);
    if (!l) return;
    _edit = l;
    _assign = new Map();
    _subs = new Map();
    UI.showScreen('screen-lineup-edit');
    document.getElementById('lineup-edit-title').textContent =
      `${UTILS.formatDate(l.match_date, 'medium')}${l.opponent ? ' · ' + l.opponent : ''}`;
    UI.setLoading('lineup-edit-content', t('common.loading'));
    try {
      const spots = await DB.lineups.getSpots(lineupId) || [];
      spots.forEach(s => {
        if (s.status === 'start' && s.slot != null) _assign.set(s.slot, s.player_id);
        else if (s.status === 'sub') _subs.set(s.player_id, (s.line || '').split(',').filter(Boolean));
      });
      _renderEditor();
    } catch (err) {
      console.error('Line-up open failed:', err);
      UI.setEmpty('lineup-edit-content', t('common.error'));
    }
  }

  function backFromEditor() { showTeam(_team, _onBack); }

  const _nameById = () => Object.fromEntries(_roster.map(p => [p.id, p.name]));
  const _shortName = (id) => {
    const n = _nameById()[id] || '?';
    const parts = n.trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : n;
  };

  // The field is drawn in a tall, narrow 100 x 150 space (field-hockey ratio),
  // so it fills the screen height. Slot y (0-100) scales to it.
  const FH = 150;
  const _fy = (y) => 3 + (y / 100) * (FH - 6);   // 0..100 -> 3..147

  // Field-hockey markings (both D's, 23m lines, centre line) come from the
  // shared field module - the same pitch the DTA field builder draws.

  // Build the field's inner SVG (starters + markings). Reused on-screen
  // (interactive) and for the downloadable image (static).
  function _fieldInner(interactive) {
    const esc = UTILS.escapeHtml;
    const slots = slotPositions(_edit.formation);
    const BH = 6.4;
    const starters = slots.map(s => {
      const pid = _assign.get(s.index);
      const cy = _fy(s.y);
      // Box width tracks the line's spacing so two same-height neighbours (the
      // central pair of an even line) never overlap.
      const BW = Math.min(21, s.gap * 0.94);
      return `
        <g ${interactive ? `class="lu-slot" data-slot="${s.index}"` : ''} style="${interactive ? 'cursor:pointer;' : ''}">
          <rect x="${s.x - BW / 2}" y="${cy - BH / 2}" width="${BW}" height="${BH}" rx="1.4"
            fill="${pid ? '#B60B2A' : 'rgba(255,255,255,0.08)'}"
            stroke="${pid ? '#fff' : 'rgba(255,255,255,0.45)'}" stroke-width="0.4"
            ${pid ? '' : 'stroke-dasharray="1.2 1.2"'}/>
          <text x="${s.x}" y="${cy + 1}" text-anchor="middle" font-size="2.8" font-weight="700"
            fill="#fff" style="pointer-events:none;">${esc(pid ? _shortName(pid) : '+')}</text>
        </g>`;
    }).join('');
    return `${hockeyMarkings()}${starters}`;
  }

  // The substitutes panel as SVG (for the exportable image): one row per tagged
  // sub - name on the left, position tags as gold pills on the right.
  function _subsExportSvg() {
    const esc = UTILS.escapeHtml;
    const subs = _roster.filter(p => (_subs.get(p.id) || []).length);
    if (!subs.length) return { svg: '', height: 0 };
    const rowH = 9;
    const rows = subs.map((p, i) => {
      const y = 10 + i * rowH;
      const tags = (_subs.get(p.id) || []).map((tg, k) => `
        <rect x="${68 + k * 10.5}" y="${y}" width="9.5" height="6" rx="1.2" fill="none" stroke="#A78F58" stroke-width="0.4"/>
        <text x="${72.75 + k * 10.5}" y="${y + 4.4}" text-anchor="middle" font-size="2.6" fill="#E4C877">${esc(SUB_ABBR[tg])}</text>`).join('');
      return `<text x="4" y="${y + 4.4}" font-size="3.1" fill="#fff">${esc(p.name)}</text>${tags}`;
    }).join('');
    const height = 10 + subs.length * rowH + 2;
    return {
      svg: `<text x="4" y="6.5" font-size="3.2" font-weight="700" fill="#A78F58">SUBSTITUTES</text>${rows}`,
      height,
    };
  }

  function _renderEditor() {
    const el = document.getElementById('lineup-edit-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;
    const size = formationSize(_edit.formation);

    const field = `
      <svg viewBox="0 0 100 150" class="lu-field" preserveAspectRatio="xMidYMid meet">
        ${_fieldInner(true)}
      </svg>`;

    // Substitutes box (under the field, and part of the exported image): every
    // non-starting player with position tags the coach can toggle.
    const placed = new Set(_assign.values());
    const subs = _roster.filter(p => !placed.has(p.id));
    const tagChip = (pid, tag) => {
      const on = (_subs.get(pid) || []).includes(tag);
      return `<button class="sel-section-toggle ${on ? 'sel-section-toggle--on' : ''}"
        data-subtag="${esc(tag)}" data-subtag-pid="${esc(pid)}">${esc(SUB_ABBR[tag])}</button>`;
    };
    const subsHtml = subs.length ? subs.map(p => `
      <div class="lu-bench-row">
        <span class="lu-bench-name">${esc(p.name)}</span>
        ${SUB_TAGS.map(tg => tagChip(p.id, tg)).join('')}
      </div>`).join('') : `<div class="sel-detail-hint">${esc(t('lu.benchEmpty'))}</div>`;

    const canShare = typeof navigator !== 'undefined' && !!navigator.share;

    el.innerHTML = `
      <div class="sel-detail-hint" style="margin-bottom:6px;">${esc(t('lu.editHint', { done: _assign.size, total: size }))}</div>
      ${field}
      <div class="sel-detail-actions" style="margin:8px 0 4px;">
        <button class="btn btn--secondary" id="lineup-download">${esc(t('lu.download'))}</button>
        ${canShare ? `<button class="btn btn--secondary" id="lineup-share">${esc(t('lu.share'))}</button>` : ''}
      </div>
      <div class="sel-detail-section__title" style="margin:14px 0 4px;">${esc(t('lu.subs'))}</div>
      <div class="sel-detail-hint" style="margin-bottom:6px;">${esc(t('lu.subsHint'))}</div>
      ${subsHtml}
      <div class="sel-detail-actions" style="margin-top:16px;">
        <button class="btn btn--primary" id="lineup-save">${esc(t('lu.save'))}</button>
        <button class="btn btn--ghost" id="lineup-delete" style="color:var(--color-red-light);">${esc(t('lu.delete'))}</button>
      </div>`;

    el.querySelectorAll('[data-slot]').forEach(g =>
      g.addEventListener('click', () => _tapSlot(Number(g.dataset.slot))));
    el.querySelectorAll('[data-subtag]').forEach(b =>
      b.addEventListener('click', () => {
        const pid = b.dataset.subtagPid, tag = b.dataset.subtag;
        const tags = _subs.get(pid) || [];
        const next = tags.includes(tag) ? tags.filter(x => x !== tag) : [...tags, tag];
        if (next.length) _subs.set(pid, next); else _subs.delete(pid);
        _renderEditor();
      }));
    document.getElementById('lineup-save')?.addEventListener('click', _save);
    document.getElementById('lineup-download')?.addEventListener('click', () => _exportImage(false));
    document.getElementById('lineup-share')?.addEventListener('click', () => _exportImage(true));
    document.getElementById('lineup-delete')?.addEventListener('click', _delete);
  }

  // ── Download / share the field as a PNG (browser-side, no server) ───────────

  function _exportSvg() {
    const cap = `${_team.id}  ${UTILS.formatDate(_edit.match_date, 'medium')}${_edit.opponent ? '  vs ' + _edit.opponent : ''}  (${_edit.formation})`;
    const capH = 14, fieldH = 150;
    const subs = _subsExportSvg();
    const total = capH + fieldH + subs.height;
    const w = 560, h = Math.round(w * total / 100);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 ${total}" width="${w}" height="${h}">
      <rect x="0" y="0" width="100" height="${total}" fill="#0f1728"/>
      <text x="50" y="7" text-anchor="middle" font-family="Arial, sans-serif" font-size="4" font-weight="700" fill="#fff">KHC Dragons</text>
      <text x="50" y="11.5" text-anchor="middle" font-family="Arial, sans-serif" font-size="2.7" fill="#A78F58">${UTILS.escapeHtml(cap)}</text>
      <g transform="translate(0,${capH})" font-family="Arial, sans-serif">${_fieldInner(false)}</g>
      ${subs.height ? `<g transform="translate(0,${capH + fieldH})" font-family="Arial, sans-serif">${subs.svg}</g>` : ''}
    </svg>`;
    return { svg, w, h };
  }

  async function _exportImage(share) {
    try {
      const { svg, w, h } = _exportSvg();
      const blob = await _svgToPng(svg, w, h);
      const fname = `lineup_${_team.id}_${_edit.match_date}.png`;
      if (share && navigator.share) {
        const file = new File([blob], fname, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `${_team.id} line-up` });
          return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Line-up image failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _svgToPng(svg, w, h) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob null')), 'image/png');
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('svg load')); };
      img.src = url;
    });
  }

  // Tap a slot: if filled, clear it; if empty, pick an unplaced player.
  function _tapSlot(slotIndex) {
    if (_assign.has(slotIndex)) { _assign.delete(slotIndex); _renderEditor(); return; }
    const placed = new Set(_assign.values());
    const options = _roster.filter(p => !placed.has(p.id));
    if (!options.length) { UI.toast(t('lu.allPlaced'), 'info'); return; }
    const esc = UTILS.escapeHtml;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" style="max-width:400px;">
        <div class="modal__title">${esc(t('lu.pickPlayer'))}</div>
        <div class="modal__body"><div class="sel-resolve-list">
          ${options.map(p => `<button class="sel-resolve-row" data-pick="${esc(p.id)}">
            <span>${esc(p.name)}</span>${p.is_goalkeeper ? '<span class="sel-resolve-dob">GK</span>' : ''}</button>`).join('')}
        </div></div>
        <div class="modal__buttons"><button class="modal-btn" data-action="cancel">${esc(t('common.cancel'))}</button></div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('[data-action="cancel"]').addEventListener('click', close);
    overlay.querySelectorAll('[data-pick]').forEach(b =>
      b.addEventListener('click', () => {
        _assign.set(slotIndex, b.dataset.pick);
        _subs.delete(b.dataset.pick);   // a starter is not a sub
        close(); _renderEditor();
      }));
  }

  async function _save() {
    const slots = slotPositions(_edit.formation);
    const bySlot = Object.fromEntries(slots.map(s => [s.index, s]));
    const spots = [];
    _assign.forEach((pid, slot) => {
      const s = bySlot[slot] || {};
      spots.push({ player_id: pid, status: 'start', slot, line: s.lineName || null, position: s.position || null });
    });
    const placed = new Set(_assign.values());
    _roster.forEach(p => {
      if (placed.has(p.id)) return;
      const tags = _subs.get(p.id) || [];
      spots.push({ player_id: p.id, status: tags.length ? 'sub' : 'out', slot: null,
        line: tags.length ? tags.join(',') : null, position: null });
    });
    try {
      await DB.lineups.saveSpots(_edit.id, spots);
      UI.toast(t('lu.saved'), 'success');
      showTeam(_team, _onBack);
    } catch (err) {
      console.error('Save line-up failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _delete() {
    UI.modal({
      title: t('lu.deleteTitle'),
      body: t('lu.deleteBody'),
      buttons: [
        { label: t('lu.delete'), style: 'danger', action: async () => {
          try {
            await DB.lineups.remove(_edit.id);
            _lineups = _lineups.filter(x => x.id !== _edit.id);
            showTeam(_team, _onBack);
          } catch (err) { console.error('Delete failed:', err); UI.toast(t('common.error'), 'error'); }
        } },
        { label: t('common.cancel') },
      ],
    });
  }

  return { showTeam, backFromList, backFromEditor };

})();
